import type { BridgeCapabilities, BridgeTransport } from "#bridge/types";
import type { SdkEnvelope } from "#envelope";
import { parseSdkEnvelope, serializeSdkEnvelope } from "#message";

type MessageHandler = (message: SdkEnvelope) => void;

type PortLike = {
	postMessage: (data: string) => void;
	start?: () => void;
	close?: () => void;
	addEventListener?: (type: "message", listener: (event: MessageEvent) => void) => void;
	removeEventListener?: (type: "message", listener: (event: MessageEvent) => void) => void;
	onmessage?: ((event: MessageEvent) => void) | null;
};

export type PortTransportOptions = {
	/** Allowed MessageEvent.origin values. Empty = accept any. */
	allowedOrigins?: readonly string[];
	capabilities?: BridgeCapabilities;
	/**
	 * How long to keep buffering outbound messages while the channel is not connected.
	 * When the timer fires without a connection, the queue is dropped and further
	 * messages are not buffered until the channel connects.
	 * `0` = never queue (drop immediately if disconnected).
	 * @default 60_000
	 */
	outboundQueueTtlMs?: number;
};

const DEFAULT_CAPABILITIES: BridgeCapabilities = {
	outbound: true,
	inbound: true,
	rpc: true,
};

/** Grace period for the race before native channel is ready. */
export const DEFAULT_OUTBOUND_QUEUE_TTL_MS = 60_000;

export type PortTransport = BridgeTransport & {
	bindPort: (port: PortLike) => void;
};

/**
 * Transport over MessagePort (TWA postMessage channel / transferred WebMessagePort)
 * or an injected WebMessageListener object (`postMessage` + `message` events).
 */
export function createPortTransport(options: PortTransportOptions = {}): PortTransport {
	const allowedOrigins = options.allowedOrigins ?? [];
	const capabilities = options.capabilities ?? DEFAULT_CAPABILITIES;
	const outboundQueueTtlMs = options.outboundQueueTtlMs ?? DEFAULT_OUTBOUND_QUEUE_TTL_MS;

	const handlers = new Set<MessageHandler>();
	const outboundQueue: SdkEnvelope[] = [];

	let port: PortLike | null = null;
	let connected = false;
	let destroyed = false;
	/** After TTL without connect — stop buffering until channel appears. */
	let queueAbandoned = false;
	let queueTtlTimer: ReturnType<typeof setTimeout> | null = null;

	const clearQueueTtlTimer = () => {
		if (queueTtlTimer != null) {
			clearTimeout(queueTtlTimer);
			queueTtlTimer = null;
		}
	};

	const dropOutboundQueue = (reason: string) => {
		const dropped = outboundQueue.length;
		outboundQueue.length = 0;
		clearQueueTtlTimer();
		if (dropped > 0) {
			console.warn(`[bridge] dropped ${dropped} queued outbound message(s) (${reason})`);
		}
	};

	const armQueueTtl = () => {
		if (queueTtlTimer != null || outboundQueueTtlMs <= 0 || destroyed) return;
		queueTtlTimer = setTimeout(() => {
			queueTtlTimer = null;
			if (connected || destroyed) return;
			dropOutboundQueue(`no channel within ${outboundQueueTtlMs}ms`);
			queueAbandoned = true;
		}, outboundQueueTtlMs);
	};

	const enqueueOutbound = (message: SdkEnvelope) => {
		if (outboundQueueTtlMs <= 0 || queueAbandoned) return;
		outboundQueue.push(message);
		armQueueTtl();
	};

	const connectWaiters: Array<{
		resolve: () => void;
		reject: (error: Error) => void;
		timer: ReturnType<typeof setTimeout>;
	}> = [];

	const isOriginAllowed = (origin: string) => {
		if (allowedOrigins.length === 0) return true;
		return allowedOrigins.includes(origin);
	};

	const dispatch = (message: SdkEnvelope) => {
		for (const handler of handlers) {
			try {
				handler(message);
			} catch (error) {
				console.error("[bridge] subscriber error", error);
			}
		}
	};

	const onPortMessage = (event: MessageEvent) => {
		const parsed = parseSdkEnvelope(event.data);
		if (!parsed) return;
		dispatch(parsed);
	};

	const bindPort = (next: PortLike) => {
		if (destroyed || port === next) return;

		if (port) {
			unbindPort(port);
		}

		port = next;
		port.start?.();

		if (typeof port.addEventListener === "function") {
			port.addEventListener("message", onPortMessage);
		} else {
			port.onmessage = onPortMessage;
		}

		connected = true;
		queueAbandoned = false;
		clearQueueTtlTimer();

		while (outboundQueue.length > 0 && port) {
			const message = outboundQueue.shift();
			if (message) {
				port.postMessage(serializeSdkEnvelope(message));
			}
		}

		for (const waiter of connectWaiters.splice(0)) {
			clearTimeout(waiter.timer);
			waiter.resolve();
		}
	};

	const unbindPort = (current: PortLike) => {
		if (typeof current.removeEventListener === "function") {
			current.removeEventListener("message", onPortMessage);
		} else if (current.onmessage === onPortMessage) {
			current.onmessage = null;
		}
	};

	const onWindowMessage = (event: MessageEvent) => {
		if (destroyed) return;
		if (!isOriginAllowed(event.origin)) return;

		const transferred = event.ports?.[0] as PortLike | undefined;
		if (transferred) {
			bindPort(transferred);
			if (event.data != null && event.data !== "") {
				const parsed = parseSdkEnvelope(event.data);
				if (parsed) dispatch(parsed);
			}
			return;
		}

		const parsed = parseSdkEnvelope(event.data);
		if (parsed) {
			dispatch(parsed);
		}
	};

	if (typeof window !== "undefined") {
		window.addEventListener("message", onWindowMessage);
	}

	return {
		capabilities,
		get connected() {
			return connected;
		},
		send(message: SdkEnvelope) {
			if (destroyed) return;
			if (!port || !connected) {
				enqueueOutbound(message);
				return;
			}
			port.postMessage(serializeSdkEnvelope(message));
		},
		subscribe(handler: MessageHandler) {
			handlers.add(handler);
			return () => handlers.delete(handler);
		},
		whenConnected(timeoutMs = 15_000) {
			if (connected) return Promise.resolve();
			if (destroyed) return Promise.reject(new Error("bridge transport destroyed"));

			return new Promise<void>((resolve, reject) => {
				const timer = setTimeout(() => {
					const index = connectWaiters.findIndex((item) => item.timer === timer);
					if (index >= 0) connectWaiters.splice(index, 1);
					reject(new Error(`bridge: channel not ready within ${timeoutMs}ms`));
				}, timeoutMs);

				connectWaiters.push({ resolve, reject, timer });
			});
		},
		destroy() {
			destroyed = true;
			connected = false;
			dropOutboundQueue("transport destroyed");
			queueAbandoned = true;

			if (typeof window !== "undefined") {
				window.removeEventListener("message", onWindowMessage);
			}

			if (port) {
				unbindPort(port);
				port.close?.();
				port = null;
			}

			for (const waiter of connectWaiters.splice(0)) {
				clearTimeout(waiter.timer);
				waiter.reject(new Error("bridge transport destroyed"));
			}

			handlers.clear();
		},
		bindPort,
	};
}
